import { useState, useEffect } from "react";
import { Login, useAuthContext } from "@mavenmm/teamwork-auth";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useAppAccess } from "../hooks/useAppAccess";
import { routes } from "../routes";
import { hasBareChrome } from "../lib/chrome";
import { AdminUserOverride } from "./AdminUserOverride";
import { FeedbackLauncher } from "@mavenmm_dev/dev-library/ui";
import { ThemeToggle } from "./ThemeToggle";

export default function Layout() {
  const { isAuthenticated, loading, logout } = useAuthContext();
  const { isRealUserDev } = useAppAccess();
  const [isScrolled, setIsScrolled] = useState(false);
  // Working surfaces (the assistant) drop the logo and Logout so they read like the
  // modal the same chat opens in, rather than a page wearing the site chrome.
  const bareChrome = hasBareChrome(useLocation().pathname, routes);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (loading) {
    return (
      <div className="h-screen flex flex-col bg-white dark:bg-maven-gray-950">
        {/* A hint of the shell rather than a spinner in the void — the nav bar and
            the hero band are the same in every theme, so showing them while auth
            resolves makes the wait feel like loading rather than a blank screen. */}
        <div className="h-20 shrink-0 bg-maven-gray-800/60" />
        <div className="flex flex-1 items-center justify-center">
          <div className="w-8 h-8 border-4 border-gray-200 dark:border-maven-gray-800 border-t-maven-teal-500 rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="h-screen flex items-center justify-center bg-white dark:bg-maven-gray-950">
        <Login
          clientID={import.meta.env.VITE_CLIENT_ID}
          redirectURI={import.meta.env.VITE_REDIRECT_URI}
        />
      </div>
    );
  }

  return (
    <div
      className={
        bareChrome
          // Bare routes own the viewport: lock the height so a flex child can pin its
          // footer to the bottom. min-h-screen would let the page grow and push the
          // composer below the fold. dvh rather than vh so mobile browser chrome does
          // not steal the last ~60px.
          ? "h-dvh overflow-hidden flex flex-col bg-white dark:bg-maven-gray-950"
          : "min-h-screen flex flex-col bg-white dark:bg-maven-gray-950"
      }
    >
      {/* Nav - Fixed, transparent at top, solid when scrolled.
          Omitted entirely on bare-chrome routes: it is position:fixed, so leaving an
          empty bar in place would still overlay the top of the page beneath it. */}
      {!bareChrome && (
        <nav className={`fixed top-0 left-0 right-0 z-40 px-6 transition-colors duration-200 ${
          isScrolled ? "bg-maven-gray-800/70 backdrop-blur-md shadow-lg" : "bg-transparent"
        }`}>
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            {/* Left: Logo */}
            <Link to="/" className="flex items-center">
              <img src="/maven_10yr_loop.gif" alt="Maven" className="h-20" />
            </Link>

            {/* Right: Nav links */}
            <div className="flex items-center gap-4">
              {/* Admin Override - only show for devs */}
              {isRealUserDev && <AdminUserOverride />}
              {/* Always white: the hero behind the nav is dark in both themes */}
              <ThemeToggle className="text-white" />
              <FeedbackLauncher variant="inverted" />
              <button
                className="text-sm px-4 py-1.5 rounded bg-white text-maven-gray-900 hover:bg-white/90"
                onClick={logout}
              >
                Logout
              </button>
            </div>
          </div>
        </nav>
      )}

      {/* No floating toggle on bare-chrome routes: the assistant is a working
          surface for testers, and the theme preference set anywhere else follows
          them here. One control, on the page that has a nav to hold it. */}

      {/* Content */}
      <Outlet />
    </div>
  );
}
